import { Injectable } from '@angular/core';
import { GetOpenEnrollmentRule, SepLifeEvent } from '../../angular-elements/form-wrappers/family-form/types/qualifying-life-event.response.interface';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure, ReducerUtilityService } from '@ptl/state';
import {
  setSelectedLifeEvent,
  clearSepFormData
} from 'src/app/shared/reducers/sep-life-event-form.reducer';
import { BrandingConfigurationService, Brand } from '@ptl/brand';

@Injectable({
  providedIn: 'root'
})
export class SepLifeEventService {

  constructor(private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private brandService: BrandingConfigurationService) { }

  /**
   * Get the selected life event ID from Redux
   * @returns The selected life event ID or null
   */
  getSelectedLifeEventId(): string | null {
    if (this.reducerUtilityService.getComponentStates().SepLifeEventForm) {
      return this.reducerUtilityService.getComponentStates().SepLifeEventForm.selectedLifeEventId;
    }
    return null;
  }

  /**
   * Get the open enrollment rules response from Redux
   * @returns The API response or null
   */
  getSpecialEnrollmentRulesResponse(): GetOpenEnrollmentRule | null {
    if (this.reducerUtilityService.getComponentStates().SepLifeEventForm) {
      return this.reducerUtilityService.getComponentStates().SepLifeEventForm.selectedLifeEvent;
    }
    return null;
  }

  /**
   * Get the life event date value from Redux
   * @returns The date value or null
   */
  getLifeEventDateValue(): string | null {
    if (this.reducerUtilityService.getComponentStates().SepLifeEventForm) {
      return this.reducerUtilityService.getComponentStates().SepLifeEventForm.lifeEventDateValue;
    }
    return null;
  }

  /**
   * Get all SEP form data from Redux
   * @returns The complete form state object
   */
  getAllSepFormData(): any {
    if (this.reducerUtilityService.getComponentStates().SepLifeEventForm) {
      return this.reducerUtilityService.getComponentStates().SepLifeEventForm;
    }
    return null;
  }
  /**
   * Find a specific life event rule by lifeEventId
   * @param rules - Array of OpenEnrollmentRule objects
   * @param lifeEventId - The life event ID to search for (e.g., 'BIRTH', 'ADOPTION')
   * @returns The matching OpenEnrollmentRule or null
   */
  findLifeEventRule(rules: GetOpenEnrollmentRule[], lifeEventId?: string | null): GetOpenEnrollmentRule | null {
    let lifeEventRule: GetOpenEnrollmentRule | null = null;
    if(lifeEventId)
      lifeEventRule = rules.find(rule => rule.lifeEventId === lifeEventId) || null;
    else
      lifeEventRule = rules[0] || null;
    
    this.ngRedux.dispatch(setSelectedLifeEvent(lifeEventRule));
    return lifeEventRule;
  }

  /**
   * Create a SepLifeEvent object from an OpenEnrollmentRule
   * @param rule - The OpenEnrollmentRule to convert
   * @param dateOfLifeEvent - The date of the life event (ISO string)
   * @param asOfDate - The as-of date (ISO string), defaults to current date
   * @returns SepLifeEvent object
   */
  createSepLifeEvent(
    rule: GetOpenEnrollmentRule,
    dateOfLifeEvent: string,
    asOfDate?: string
  ): SepLifeEvent {
    return {
      lifeEventId: rule.lifeEventId,
      dateOfLifeEvent: this.formatDateToISO(dateOfLifeEvent),
      effectiveDateRuleId: rule.effectiveDateRuleId,
      asOfDate: asOfDate ? this.formatDateToISO(asOfDate) : this.formatDateToISO(new Date().toISOString()),
      effectiveDateRuleConfig: rule.effectiveDateRuleConfig
    };
  }

  /**
   * Format date to ISO 8601 format
   * @param date - Date string or Date object
   * @returns ISO formatted date string
   */
  private formatDateToISO(date: string | Date): string {
    if (typeof date === 'string') {
      // If already a string, ensure it's in ISO format
      const dateObj = new Date(date);
      return dateObj.toISOString();
    }
    return date.toISOString();
  }

  /**
   * Get all available life event IDs from rules
   * @param rules - Array of OpenEnrollmentRule objects
   * @returns Array of unique life event IDs
   */
  getAvailableLifeEventIds(rules: GetOpenEnrollmentRule[]): string[] {
    return [...new Set(rules.map(rule => rule.lifeEventId))];
  }

  /**
   * Get life event description by ID
   * @param rules - Array of OpenEnrollmentRule objects
   * @param lifeEventId - The life event ID
   * @returns The life event description or null
   */
  getLifeEventDescription(rules: GetOpenEnrollmentRule[], lifeEventId: string): string | null {
    const rule = this.findLifeEventRule(rules, lifeEventId);
    return rule ? rule.lifeEventDescription : null;
  }

  /**
     * Get the SepLifeEvent object from Redux data
     * Useful if you only need the sepLifeEvent part
     * @returns SepLifeEvent or null
     */
    getSepLifeEventFromReduxData(): SepLifeEvent | null {
      const brand: Brand = this.brandService.getCurrentBrand();
      try {
        const selectedLifeEventId = this.getSelectedLifeEventId();
        const selectedLifeEvent = this.getSpecialEnrollmentRulesResponse();
        const lifeEventDateValue = this.getLifeEventDateValue();
  
        if ((!selectedLifeEventId || !selectedLifeEvent || !lifeEventDateValue) && brand.region === 'WY') {
          return null;
        }
  
        if (!selectedLifeEvent) {
          return null;
        }
  
        return this.createSepLifeEvent(selectedLifeEvent, lifeEventDateValue);
      } catch (error) {
        console.error('Error getting SepLifeEvent from Redux data:', error);
        return null;
      }
    }
  
    /**
     * Clear all SEP form data from Redux
     */
    clearSepFormData(): void {
      this.ngRedux.dispatch(clearSepFormData());
    }
  
    /**
     * Check if all required data is available in Redux
     * @returns true if all required data is present
     */
    isAllRequiredDataAvailable(): boolean {
      return !!(
        this.getSelectedLifeEventId() &&
        this.getSpecialEnrollmentRulesResponse() &&
        this.getLifeEventDateValue()
      );
    }
}